import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
  HiChartBar as Analytics,
  HiCalendar as Calendar,
  HiCheckCircle as CheckCircle,
  HiTrash as Delete,
  HiDownload as Download,
  HiPencil as Edit,
  HiXCircle as ErrorIcon,
  HiChevronDown as ExpandMore,
  HiViewGrid as GridIcon,
  HiInformationCircle as Info,
  HiClipboardList as ListIcon,
  HiLightningBolt as Optimize,
  HiPause as Pause,
  HiPlay as PlayArrow,
  HiCog as Settings,
  HiStop as Stop,
  HiViewList as Timeline,
  HiEye as Visibility,
  HiExclamationCircle as WarningIcon,
} from 'react-icons/hi';
import { mockData } from './MockData';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`generation-tabpanel-${index}`}
      aria-labelledby={`generation-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TimetableGenerationManagement: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [_showViewDialog, setShowViewDialog] = useState(false);
  const [newConfig, setNewConfig] = useState<any>({});
  const [configs, setConfigs] = useState(mockData.generationConfigs);
  const [currentProgress, setCurrentProgress] = useState(mockData.currentProgress);
  const [conflicts] = useState(mockData.conflicts);
  const [timetableViews] = useState(mockData.timetableViews);
  const [generationResults] = useState(mockData.generationResults);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
        return 'info';
      case 'failed':
        return 'error';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'major':
        return 'warning';
      case 'minor':
        return 'info';
      case 'warning':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getConflictStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle color="success" />;
      case 'resolving':
        return <CircularProgress size={20} />;
      case 'unresolved':
        return <WarningIcon color="warning" />;
      case 'ignored':
        return <Info color="disabled" />;
      default:
        return <WarningIcon />;
    }
  };

  const handleStartGeneration = (_configId: string) => {
    setIsGenerating(true);
    // Simulate generation progress
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 10;
      if (progress >= 100) {
        progress = 100;
        setIsGenerating(false);
        clearInterval(interval);
      }
      setCurrentProgress({
        ...currentProgress,
        progressPercentage: Math.min(progress, 100),
      });
    }, 1000);
  };

  const handleAddConfig = () => {
    const configWithId = {
      ...newConfig,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      status: 'draft',
    };
    setConfigs([...configs, configWithId]);
    setNewConfig({});
    setShowConfigDialog(false);
  };

  const renderGenerationControl = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Generation Configurations</Typography>
              <Button startIcon={<Settings />} onClick={() => setShowConfigDialog(true)}>
                New Config
              </Button>
            </Box>

            {configs.map((config) => (
              <Card key={config.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1">{config.name}</Typography>
                    <Chip label={config.status} color={getStatusColor(config.status) as any} size="small" />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Type: {config.type} | Priority: {config.scope.priority}
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Optimization Level:
                    </Typography>
                    <Chip label={config.parameters.optimizationLevel} size="small" sx={{ ml: 1 }} />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Created: {new Date(config.createdAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                      Est. Duration: {config.estimatedDuration}min
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      {config.scope.yearGroups && (
                        <Chip label={`${config.scope.yearGroups.length} year groups`} size="small" variant="outlined" />
                      )}
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={config.status === 'running' || isGenerating}
                        onClick={() => handleStartGeneration(config.id)}
                      >
                        <PlayArrow />
                      </IconButton>
                      <IconButton size="small" color="primary">
                        <Edit />
                      </IconButton>
                      <IconButton size="small" color="error">
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Generation Progress
            </Typography>

            {isGenerating || currentProgress.status !== 'completing' ? (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">
                    {configs.find((c) => c.id === currentProgress.configId)?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentProgress.progressPercentage.toFixed(0)}%
                  </Typography>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={currentProgress.progressPercentage}
                  sx={{ mb: 2, height: 8, borderRadius: 4 }}
                />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Current Step: {currentProgress.currentStep}
                </Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {currentProgress.lessonsScheduled}
                      </Typography>
                      <Typography variant="caption">Lessons Scheduled</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="warning.main">
                        {currentProgress.conflictsDetected}
                      </Typography>
                      <Typography variant="caption">Conflicts Detected</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Started: {new Date(currentProgress.startTime).toLocaleTimeString()}
                  </Typography>
                  <Box>
                    <IconButton size="small" color="warning">
                      <Pause />
                    </IconButton>
                    <IconButton size="small" color="error">
                      <Stop />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Alert severity="info" icon={<Info />}>
                No generation currently in progress. Select a configuration and click play to start.
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Recent Activity Log
            </Typography>
            <List dense>
              {currentProgress.logs.slice(-5).map((log, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    {log.level === 'error' ? (
                      <ErrorIcon color="error" />
                    ) : log.level === 'warning' ? (
                      <WarningIcon color="warning" />
                    ) : (
                      <Info color="info" />
                    )}
                  </ListItemIcon>
                  <ListItemText primary={log.message} secondary={new Date(log.timestamp).toLocaleTimeString()} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderConflictDashboard = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Conflict Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.50' }}>
                  <Typography variant="h3" color="error.main">
                    {conflicts.filter((c) => c.severity === 'critical').length}
                  </Typography>
                  <Typography variant="body2">Critical</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
                  <Typography variant="h4" color="warning.main">
                    {conflicts.filter((c) => c.severity === 'major').length}
                  </Typography>
                  <Typography variant="caption">Major</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
                  <Typography variant="h4" color="info.main">
                    {conflicts.filter((c) => c.severity === 'minor').length}
                  </Typography>
                  <Typography variant="caption">Minor</Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Active Conflicts
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Conflict</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conflicts.map((conflict) => (
                    <TableRow key={conflict.id}>
                      <TableCell>
                        <Typography variant="body2">{conflict.description}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {conflict.affectedEntities.rooms?.join(', ') ||
                            conflict.affectedEntities.teachers?.join(', ')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={conflict.type} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={conflict.severity}
                          color={getSeverityColor(conflict.severity) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{getConflictStatusIcon(conflict.currentStatus)}</TableCell>
                      <TableCell>
                        <IconButton size="small" color="primary">
                          <Visibility />
                        </IconButton>
                        {conflict.currentStatus === 'unresolved' && (
                          <IconButton size="small" color="warning">
                            <Settings />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Resolution Options
            </Typography>
            {conflicts
              .filter((c) => c.currentStatus === 'unresolved')
              .map((conflict) => (
                <Accordion key={conflict.id}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <WarningIcon color="warning" style={{ marginRight: 8 }} />
                      <Typography variant="subtitle2">{conflict.description}</Typography>
                      <Chip
                        label={conflict.severity}
                        color={getSeverityColor(conflict.severity) as any}
                        size="small"
                        sx={{ ml: 'auto' }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {conflict.resolutionOptions.map((option) => (
                        <Grid item xs={12} md={6} key={option.id}>
                          <Card variant="outlined">
                            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                              <Box
                                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
                              >
                                <Typography variant="subtitle2">{option.description}</Typography>
                                <Chip
                                  label={`${(option.feasibilityScore * 100).toFixed(0)}%`}
                                  size="small"
                                  color={option.feasibilityScore > 0.8 ? 'success' : 'warning'}
                                />
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Strategy: {option.strategy}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Impact: {option.impactAssessment.affectedLessons} lessons affected
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Chip
                                  label={option.automaticallyApplicable ? 'Auto-apply' : 'Manual'}
                                  size="small"
                                  color={option.automaticallyApplicable ? 'success' : 'default'}
                                />
                                <Button size="small" variant="outlined">
                                  Apply
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderTimetableViews = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Timetable Views</Typography>
              <Button startIcon={<Calendar />} onClick={() => setShowViewDialog(true)}>
                New View
              </Button>
            </Box>

            {timetableViews.map((view) => (
              <Card key={view.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {view.name}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Chip label={view.type} size="small" sx={{ mr: 1 }} />
                    <Chip label={view.timeframe} size="small" variant="outlined" />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Layout: {view.displayOptions.layout} | Colors: {view.displayOptions.colorScheme}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Updated: {new Date(view.lastUpdated).toLocaleDateString()}
                    </Typography>
                    <Box>
                      <IconButton size="small" color="primary">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small" color="primary">
                        <Edit />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Sample Timetable View</Typography>
              <Box>
                <IconButton color="primary">
                  <GridIcon />
                </IconButton>
                <IconButton>
                  <Timeline />
                </IconButton>
                <IconButton>
                  <ListIcon />
                </IconButton>
                <IconButton color="primary">
                  <Download />
                </IconButton>
              </Box>
            </Box>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Monday</TableCell>
                    <TableCell>Tuesday</TableCell>
                    <TableCell>Wednesday</TableCell>
                    <TableCell>Thursday</TableCell>
                    <TableCell>Friday</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {['Period 1', 'Period 2', 'Break', 'Period 3', 'Period 4', 'Lunch', 'Period 5'].map((period) => (
                    <TableRow key={period}>
                      <TableCell sx={{ fontWeight: 'bold' }}>{period}</TableCell>
                      <TableCell>
                        {period !== 'Break' && period !== 'Lunch' && (
                          <Card variant="outlined" sx={{ p: 1, bgcolor: 'primary.50' }}>
                            <Typography variant="caption" display="block">
                              Mathematics
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              S. Johnson | M01
                            </Typography>
                          </Card>
                        )}
                      </TableCell>
                      <TableCell>
                        {period !== 'Break' && period !== 'Lunch' && (
                          <Card variant="outlined" sx={{ p: 1, bgcolor: 'secondary.50' }}>
                            <Typography variant="caption" display="block">
                              Chemistry
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              E. Chen | SL01
                            </Typography>
                          </Card>
                        )}
                      </TableCell>
                      <TableCell>
                        {period !== 'Break' && period !== 'Lunch' && (
                          <Card variant="outlined" sx={{ p: 1, bgcolor: 'success.50' }}>
                            <Typography variant="caption" display="block">
                              English Lit
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              J. Wilson | E01
                            </Typography>
                          </Card>
                        )}
                      </TableCell>
                      <TableCell>
                        {period !== 'Break' && period !== 'Lunch' && (
                          <Card variant="outlined" sx={{ p: 1, bgcolor: 'warning.50' }}>
                            <Typography variant="caption" display="block">
                              History
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              M. Brown | H01
                            </Typography>
                          </Card>
                        )}
                      </TableCell>
                      <TableCell>
                        {period !== 'Break' && period !== 'Lunch' && (
                          <Card variant="outlined" sx={{ p: 1, bgcolor: 'info.50' }}>
                            <Typography variant="caption" display="block">
                              PE
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              R. Taylor | Sports Hall
                            </Typography>
                          </Card>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderAnalytics = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Generation Statistics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                  <Typography variant="h3" color="success.main">
                    {generationResults.statistics.scheduledLessons}
                  </Typography>
                  <Typography variant="body2">Lessons Scheduled</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
                  <Typography variant="h3" color="warning.main">
                    {generationResults.statistics.unscheduledLessons}
                  </Typography>
                  <Typography variant="body2">Unscheduled</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
                  <Typography variant="h3" color="info.main">
                    {generationResults.statistics.optimizationScore}
                  </Typography>
                  <Typography variant="body2">Optimization Score</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
                  <Typography variant="h3" color="primary.main">
                    {generationResults.statistics.executionTime}m
                  </Typography>
                  <Typography variant="body2">Generation Time</Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Resource Utilization
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Room Utilization</Typography>
                <Typography variant="body2">{generationResults.finalTimetable.summary.roomUtilization}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={generationResults.finalTimetable.summary.roomUtilization}
                color="primary"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Teacher Utilization</Typography>
                <Typography variant="body2">{generationResults.finalTimetable.summary.teacherUtilization}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={generationResults.finalTimetable.summary.teacherUtilization}
                color="secondary"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">Utilization rates are within optimal range (80-95%)</Typography>
            </Alert>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Optimization Recommendations
            </Typography>
            <List>
              {generationResults.recommendations.map((recommendation, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Optimize color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={recommendation} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Alternative Timetables
            </Typography>
            <Grid container spacing={2}>
              {generationResults.alternatives.map((alternative) => (
                <Grid item xs={12} md={6} key={alternative.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1">{alternative.name}</Typography>
                        <Chip
                          label={`Score: ${alternative.score}`}
                          color={alternative.score > 8.5 ? 'success' : 'warning'}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Key Differences:
                      </Typography>
                      <List dense>
                        {alternative.differences.map((diff, idx) => (
                          <ListItem key={idx}>
                            <ListItemText primary={diff} primaryTypographyProps={{ variant: 'body2' }} />
                          </ListItem>
                        ))}
                      </List>
                      <Button size="small" variant="outlined" sx={{ mt: 1 }}>
                        Compare
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Timetable Generation & Management
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Generate, optimize, and manage automated timetables with comprehensive conflict resolution
      </Typography>

      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="generation management tabs">
            <Tab icon={<Settings />} label="Generation Control" />
            <Tab icon={<WarningIcon />} label="Conflict Dashboard" />
            <Tab icon={<Calendar />} label="Timetable Views" />
            <Tab icon={<Analytics />} label="Analytics" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {renderGenerationControl()}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {renderConflictDashboard()}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {renderTimetableViews()}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            {renderAnalytics()}
          </TabPanel>
        </CardContent>
      </Card>

      {/* Add Configuration Dialog */}
      <Dialog open={showConfigDialog} onClose={() => setShowConfigDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Generation Configuration</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Configuration Name"
                value={newConfig.name || ''}
                onChange={(e) => setNewConfig({ ...newConfig, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Generation Type</InputLabel>
                <Select
                  value={newConfig.type || ''}
                  onChange={(e) => setNewConfig({ ...newConfig, type: e.target.value })}
                >
                  <MenuItem value="full">Full School</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                  <MenuItem value="year-group">Year Group</MenuItem>
                  <MenuItem value="subject-specific">Subject Specific</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Optimization Level</InputLabel>
                <Select
                  value={newConfig.optimizationLevel || ''}
                  onChange={(e) =>
                    setNewConfig({
                      ...newConfig,
                      parameters: { ...newConfig.parameters, optimizationLevel: e.target.value },
                    })
                  }
                >
                  <MenuItem value="basic">Basic</MenuItem>
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newConfig.priority || ''}
                  onChange={(e) =>
                    setNewConfig({
                      ...newConfig,
                      scope: { ...newConfig.scope, priority: e.target.value },
                    })
                  }
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Iterations"
                type="number"
                value={newConfig.maxIterations || ''}
                onChange={(e) =>
                  setNewConfig({
                    ...newConfig,
                    parameters: { ...newConfig.parameters, maxIterations: Number.parseInt(e.target.value) },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Timeout (minutes)"
                type="number"
                value={newConfig.timeoutMinutes || ''}
                onChange={(e) =>
                  setNewConfig({
                    ...newConfig,
                    parameters: { ...newConfig.parameters, timeoutMinutes: Number.parseInt(e.target.value) },
                  })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfigDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddConfig}>
            Create Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimetableGenerationManagement;
